<script setup lang="ts">
import { ref, onMounted } from 'vue';

/**
 * Opt-in cookie consent banner. Defaults to "not set" → tracking disabled.
 * User either clicks Accept (→ POSTs grant, starts tracking) or Decline
 * (→ POSTs revoke, deletes any existing data, clears cookie).
 *
 * Persists the choice in localStorage under `bmcs_consent` so the banner
 * only shows on first visit. The footer "Manage cookie preferences" link
 * dispatches a `bmcs-consent-reset` event to re-show this banner.
 */

const visible = ref(false);
const saveError = ref(false);
const saving = ref(false);

function showBanner() {
  saveError.value = false;
  visible.value = true;
}

onMounted(() => {
  const stored = localStorage.getItem('bmcs_consent');
  if (stored !== 'grant' && stored !== 'revoke') {
    // Small delay so the banner doesn't flash up during the hero load animation.
    setTimeout(showBanner, 600);
  }

  // Footer "Manage cookie preferences" link dispatches this event.
  window.addEventListener('bmcs-consent-reset', showBanner);
});

async function decide(action: 'grant' | 'revoke') {
  saveError.value = false;
  saving.value = true;
  try {
    const res = await fetch('/api/consent.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ action }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } catch {
    // Surface the error — do NOT persist localStorage so the choice isn't
    // recorded locally without a matching server record.
    saveError.value = true;
    saving.value = false;
    return;
  }
  saving.value = false;
  localStorage.setItem('bmcs_consent', action);
  visible.value = false;
}
</script>

<template>
  <Transition name="consent-fade">
    <div v-if="visible" class="consent" role="dialog" aria-live="polite" aria-label="Cookie preferences">
      <div class="consent-inner">
        <p class="consent-body">
          We use a first-party cookie to understand how visitors use our site
          and to connect contact-form submissions with a visit history. No
          third-party trackers. See our
          <RouterLink to="/legal">Privacy Policy</RouterLink> for details.
        </p>
        <div class="consent-actions">
          <button type="button" class="btn-ghost-light" :disabled="saving" @click="decide('revoke')">Decline</button>
          <button type="button" class="btn-primary" :disabled="saving" @click="decide('grant')">Accept</button>
        </div>
      </div>
      <p v-if="saveError" class="consent-error" role="alert">
        Couldn't save your choice — please try again.
      </p>
    </div>
  </Transition>
</template>

<style scoped>
.consent {
  position: fixed; left: 16px; right: 16px; bottom: 16px;
  z-index: 100;
  background: var(--bmcs-ink-900);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 12px;
  box-shadow: 0 24px 60px -20px rgba(0,0,0,0.6);
  max-width: 720px;
  margin: 0 auto;
}
.consent-inner {
  display: flex; align-items: center; gap: 24px;
  padding: 18px 22px;
  flex-wrap: wrap;
}
.consent-body {
  flex: 1 1 320px;
  margin: 0;
  font-size: 13.5px; line-height: 1.5;
  color: rgba(255,255,255,0.82);
}
.consent-body a { color: var(--bmcs-signal-500); }
.consent-actions { display: flex; gap: 10px; flex: 0 0 auto; }
.btn-ghost-light {
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 13px;
  cursor: pointer;
  transition: background var(--bmcs-dur-fast) var(--bmcs-ease);
}
.btn-ghost-light:hover { background: rgba(255,255,255,0.08); }
.btn-primary {
  background: var(--bmcs-signal-500);
  color: #fff;
  border: 0;
  border-radius: 999px;
  padding: 8px 22px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--bmcs-dur-fast) var(--bmcs-ease);
}
.btn-primary:hover { background: #66ccff; }

/* Transition */
.consent-fade-enter-active, .consent-fade-leave-active {
  transition: opacity 280ms ease, transform 280ms cubic-bezier(.12,.23,.5,1);
}
.consent-fade-enter-from, .consent-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.consent-error {
  margin: 0;
  padding: 8px 22px 14px;
  font-size: 12.5px;
  color: #ff8080;
}
button:disabled { opacity: 0.5; cursor: not-allowed; }

@media (max-width: 620px) {
  .consent-inner { padding: 16px; }
  .consent-actions { flex: 1 1 100%; justify-content: stretch; }
  .consent-actions button { flex: 1; }
}
</style>
